/**
 * useTraffback — WEB-256 B3 traffback hook.
 *
 * Intercepts the browser back-button (popstate) and sends the visitor to the
 * target the funnel author picked (WEB-2141: app store by OS / a custom
 * address / deliberately nowhere). Default-ON, no FE toggle.
 *
 * Behaviour:
 *   - Fires TRAFFBACK analytics event (keepalive) via trackEvent.
 *   - Redirects to iosStoreUrl (OS=ios) or androidStoreUrl (OS=android).
 *   - No-op when: OS=other (desktop), or matching URL is null/undefined.
 *   - Deduplicates: fires once per browser session (sessionStorage flag).
 *   - Back-trap: pushes one sentinel history entry on mount so the first Back
 *     press pops it (same-document → popstate fires reliably, incl. iOS) instead
 *     of unloading a single-entry ad-landing (WEB-256 hard-review).
 *   - Cleans up listeners on unmount.
 *   - No-op in preview-link mode (WEB-2115): no sentinel entry, no listener.
 *   - WEB-2141 б (решение владельца 03.11): уводит только посетителя, который
 *     уже уходил со страницы (`pagehide` → пометка в хранилище вкладки) и
 *     вернулся. Без пометки «назад» посреди квиза — обычный шаг назад
 *     браузера (`history.back()`), событие TRAFFBACK не пишется.
 */
import { useEffect, useRef } from 'react';
import { detectOs } from '@/player/sources/ua-detect';
import { trackEvent } from './trackEvent';
import type { TrackTarget } from './trackEvent';
import { DEFAULT_RETURN_TARGET, type ReturnTarget } from './returnTarget';
import { usePreviewLinkMode } from '@/components/public/preview/previewLinkMode';

const DEDUP_KEY = 'traffback_fired';
// WEB-2141 б: страница этой вкладки уже уходила (`pagehide`).
const LEFT_KEY = 'traffback_left';

export interface UseTraffbackOptions {
  target: TrackTarget;
  iosStoreUrl: string | null | undefined;
  androidStoreUrl: string | null | undefined;
  /**
   * WEB-2141 — КУДА вести вернувшегося (настройка воронки, решение владельца
   * 16.09.2026). Не передали — `store`, прежнее поведение: у ловушки есть
   * второй вызывающий (публичный экран оплаты), и менять его молча нельзя.
   */
  returnTarget?: ReturnTarget;
}

export function useTraffback({
  target,
  iosStoreUrl,
  androidStoreUrl,
  returnTarget,
}: UseTraffbackOptions): void {
  // firedRef: in-memory guard so we don't fire multiple times even if sessionStorage
  // is unavailable (private browsing on some iOS Safari versions).
  const firedRef = useRef(false);
  // trappedRef: ensure the back-trap history entry is pushed exactly once.
  const trappedRef = useRef(false);
  // WEB-2141 б: пометка ухода в памяти — на случай недоступного хранилища.
  const leftRef = useRef(false);
  // Режим ссылки предпросмотра (WEB-2115): проход не боевой — ловушка не
  // ставится вовсе. Страница получателя глушит само нажатие «назад»
  // (`pages/PublicPreview.tsx`, вторая линия), но запись в историю ловушка
  // добавляла при каждом создании плеера: первое «назад» молча съедалось, а
  // каждая смена версии добавляла ещё одну запись.
  const previewLink = usePreviewLinkMode();

  useEffect(() => {
    if (previewLink) return;
    function handleBack(): void {
      // Dedup: one traffback per session
      if (firedRef.current) return;
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DEDUP_KEY)) return;

      // WEB-2141 б: страница не уходила — это «назад» посреди квиза, а не
      // возврат с чужой страницы: не считаем и не уводим, отдаём нажатие
      // браузеру (прошлая страница), чтобы оно не пропало молча.
      let left = leftRef.current;
      try { left = left || sessionStorage.getItem(LEFT_KEY) === '1'; } catch { /* private browsing */ }
      if (!left) {
        window.history.back();
        return;
      }

      // WEB-2124: СОБЫТИЕ — ПРО НАЖАТИЕ «НАЗАД», переход в магазин — отдельное
      // следствие, которого может не быть. До этой правки выход «адреса
      // магазина нет» стоял ЗДЕСЬ, перед отметкой и излучением, и возврат не
      // фиксировался вовсе у всех, кто магазин не заполнил (клиенты «только
      // квизы» — большинство) и на десктопе: показатель «возвраты» у них был
      // ровно ноль, и отличить «никто не возвращается» от «мы не считаем»
      // было нечем. Порядок ниже — отметка, событие, и только потом проверка
      // адреса: иначе повторные нажатия без магазина задваивали бы событие.
      //
      // Mark as fired (both in-memory and sessionStorage for cross-tab/bfcache safety)
      firedRef.current = true;
      try { sessionStorage.setItem(DEDUP_KEY, '1'); } catch { /* private browsing */ }

      // Fire analytics event with keepalive (survives page unload)
      trackEvent(target, 'TRAFFBACK', {});

      // WEB-2141 — КУДА уводить, решает автор воронки. Три варианта решения
      // владельца 16.09.2026, и ровно они:
      //   · `stay`  — осознанный отказ от перехвата: остаёмся на экране.
      //     Событие выше уже посчитано — именно этот случай раньше и терялся;
      //   · `url`   — произвольный адрес. Работает и на ДЕСКТОПЕ: до этой
      //     карточки ловушка там молчала вовсе, и десктоп был слепой зоной;
      //   · `store` — прежнее поведение: магазин по ОС посетителя, а если
      //     адреса под его ОС нет — остаёмся (уводить человека с десктопа в
      //     мобильный магазин по нажатию «назад» нельзя, см. комментарий
      //     `resolveTraffbackStoreUrl` выше).
      //
      // Адрес уже прошёл дверь схем при чтении настроек
      // (`analytics/returnTarget.ts`): негодный туда не доезжает, он
      // превращается в `stay`. Здесь второй проверки нет НАМЕРЕННО — правило
      // одно и живёт в одном месте (шрам WEB-1687).
      const effective = returnTarget ?? DEFAULT_RETURN_TARGET;

      if (effective.kind === 'stay') return;

      /**
       * Адрес ухода: свой адрес автора либо магазин по ОС посетителя.
       *
       * Магазин здесь — для ОБРАТНОЙ ЛОВУШКИ трафика (WEB-256 B3), не для
       * кнопки в приложение.
       *
       * ВНИМАНИЕ (ревизия пачки 19): это НЕ правило выбора магазина WEB-1910
       * (`pickStoreUrlForPlatform` на бэке ↔ `resolveStoreUrl` в
       * `utils/openAppOrStore.ts` на фронте, письменный ответ —
       * `backend/src/projects/utils/store-url-rule.cases.json`). Там «своего
       * адреса нет → ведём в имеющийся соседний», здесь СОЗНАТЕЛЬНО иначе:
       * нераспознанная ОС и десктоп дают `null` и ловушка молчит — уводить
       * человека с десктопа в мобильный магазин по нажатию «назад» нельзя.
       * Второго конца у ЭТОГО выбора нет по построению (правило одноместное),
       * поэтому сторожа ему не заводим — сторож, который не может упасть,
       * хуже отсутствующего (CLAUDE.md, WEB-1844).
       *
       * Одним выражением, а не отдельной функцией с двумя ветками ухода: файл
       * едет в первом экране покупателя, и этой записью оплачен признак
       * режима ссылки предпросмотра выше (замер веса 06.10.2026).
       */
      const os = detectOs(navigator.userAgent);
      const url =
        effective.kind === 'url'
          ? effective.url
          : os === 'ios'
            ? iosStoreUrl
            : os === 'android'
              ? androidStoreUrl
              : null;
      // Уводить некуда (пустой адрес, десктоп, нераспознанная ОС,
      // незаполненный магазин) — событие уже посчитано, перехода просто нет.
      if (url) window.location.assign(url);
    }

    // WEB-256 (hard-review): back-trap. A fresh ad-landing has a single history
    // entry, so the first Back press unloads the document and popstate never
    // fires — traffback is lost. Push a sentinel entry so the first Back pops it
    // (same-document navigation → popstate fires reliably, incl. iOS), keeping
    // the user on the page to redirect to the store.
    //
    // NB: the previous `pageshow persisted=true` fallback was REMOVED — it fired
    // on ANY bfcache restore (tab switch, forward-nav back to the quiz; near-
    // universal on iOS), force-yanking the user to the store without a real Back.
    // With the back-trap, popstate is reliable and bfcache guessing is unneeded.
    if (!trappedRef.current) {
      trappedRef.current = true;
      try {
        window.history.pushState({ traffback: true }, '');
      } catch {
        /* history unavailable — degrade to listener-only */
      }
    }

    function handleLeave(): void {
      leftRef.current = true;
      try { sessionStorage.setItem(LEFT_KEY, '1'); } catch { /* private browsing */ }
    }

    window.addEventListener('popstate', handleBack);
    window.addEventListener('pagehide', handleLeave);

    return () => {
      window.removeEventListener('popstate', handleBack);
      window.removeEventListener('pagehide', handleLeave);
    };
  }, [target, iosStoreUrl, androidStoreUrl, returnTarget, previewLink]);
}
